import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot } from '@angular/router';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  Subject,
  withLatestFrom,
} from 'rxjs';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { RouteSnapshotService } from '@cosmos/router';
import { injectDestroyRef } from '@cosmos/util-common/di';

import type { TrackEvent, TrackEventData } from './track-event';
import type { UserEvent } from './user-event';

@Injectable({ providedIn: 'root' })
export class CosAnalyticsService {
  private readonly _routeSnapshot = inject(RouteSnapshotService);

  readonly #trackEvent$ = new Subject<Readonly<TrackEvent<TrackEventData>>>();
  readonly trackEvent$ = this.#trackEvent$.asObservable();

  // `userEvent$` is set through the identify method
  // that is called from the root component of the application whenever
  // the user is authenticated. Its value includes `userId` and `traits`
  // (properties of the user, such as name, email, family name, company name, etc.).
  readonly #userEvent$ = new BehaviorSubject<Readonly<UserEvent> | null>(null);
  readonly userEvent$ = this.#userEvent$.asObservable();
  readonly pageEvent$ = this._getPageEvents$();

  enabled = true;
  globalOverrides: Readonly<TrackEventData> = {};

  constructor() {
    injectDestroyRef().onDestroy(() => {
      this.#userEvent$.complete();
      this.#userEvent$.unsubscribe();
      this.#trackEvent$.complete();
      this.#trackEvent$.unsubscribe();
    });
  }

  get user(): Readonly<UserEvent> | null {
    return this.#userEvent$.value;
  }

  track(event: TrackEvent<TrackEventData>) {
    if (!this.enabled) {
      return;
    }

    const properties = this._getBaseEventProperties(event);

    this.#trackEvent$.next({
      properties,
      action: event.action,
      statDestination: event.statDestination,
    });
  }

  identify(data: Readonly<UserEvent> | null) {
    // A reset (`identify(null)`) must always clear the user context, even when
    // analytics is disabled — otherwise downstream integrations (Refiner,
    // Appcues, …) keep reading a stale `userEvent$` for the previous user
    // (e.g. after a support-user session set `enabled = false`). The `enabled`
    // gate only suppresses identifying a *new active* user.
    if (data !== null && !this.enabled) {
      return;
    }

    this.#userEvent$.next(data);
  }

  private _getBaseEventProperties(
    event: TrackEvent<TrackEventData>
  ): TrackEventData {
    const user = this.user?.traits;
    const pageUrlName =
      event?.properties?.pageUrlName ??
      this._routeSnapshot.snapshot$.value?.data?.['analytics']?.page;
    const referrerPageUrlName =
      event?.properties?.referrerPageUrlName ??
      this._routeSnapshot.previousSnapshot$.value?.data?.['analytics']?.page;

    return {
      ...event.properties,
      appCode: user?.['TenantCode'],
      appId: user?.['TenantId'],
      pageUrlName,
      referrerPageUrlName,
      nativeApp: false,
      ...this.globalOverrides,
    };
  }

  private _getPageEvents$(): Observable<{
    snapshot: ActivatedRouteSnapshot;
    previousReferrerUrl: string | null;
  }> {
    // multicasting is not needed on this stream since "_routeSnapshot" instances we use here are already observables
    return this._routeSnapshot.snapshot$.pipe(
      filter((snapshot): snapshot is ActivatedRouteSnapshot => {
        return (
          snapshot != null &&
          this.enabled &&
          !snapshot.data?.['analytics']?.doNotCaptureTheEvent
        );
      }),
      withLatestFrom(this._routeSnapshot.previousReferrerUrl$),
      map(([snapshot, previousReferrerUrl]) => ({
        snapshot,
        previousReferrerUrl,
      }))
    );
  }
}
